import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { PrismIcon, PrismTooltip } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { isEmpty, not, or } from 'ember-truth-helpers';
import GeneralSmartInsightsDetailViewSmartInsightKpiDelta, {
  deltaColorClass,
} from 'lumen/components/general/smart-insights/detail-view/smart-insight-kpi-delta';
import GeneralSmartInsightsDetailViewSmartInsightKpiHoverPanel, {
  type KpiHoverData,
} from 'lumen/components/general/smart-insights/detail-view/smart-insight-kpi-hover-panel';
import type { SmartInsightDayInsightEntrySource } from 'lumen/models/smart-insights/smart-insight-day-insight-entry';
import type { MarketDemandLevel } from 'lumen/utils/constants/general-constants';
import {
  MARKET_DEMAND_LEVEL_NUMBERS,
  MARKET_DEMAND_LEVELS_CLASSES,
} from 'lumen/utils/constants/general-constants';

import styles from './smart-insight-kpi-block.module.css';

interface GeneralSmartInsightsDetailViewSmartInsightKpiBlockSignature {
  Args: {
    changeValue?: number | null;
    changeValueFormatted?: string;
    demandLevel?: MarketDemandLevel | null;
    hover?: KpiHoverData;
    isChangeValueHidden?: boolean;
    isPaceValueHidden?: boolean;
    label: string;
    onSourceClick: (
      source: SmartInsightDayInsightEntrySource,
      event: MouseEvent,
    ) => void;
    paceValue?: number;
    paceValueFormatted?: string;
    source?: SmartInsightDayInsightEntrySource | null;
    value: string;
  };
}

export default class GeneralSmartInsightsDetailViewSmartInsightKpiBlock extends Component<GeneralSmartInsightsDetailViewSmartInsightKpiBlockSignature> {
  get demandLevelClass(): string {
    const { demandLevel } = this.args;

    if (!demandLevel) {
      return '';
    }

    return MARKET_DEMAND_LEVELS_CLASSES[demandLevel];
  }

  get demandLevelNumber(): number | null {
    const { demandLevel } = this.args;

    if (!demandLevel) {
      return null;
    }

    return MARKET_DEMAND_LEVEL_NUMBERS[demandLevel];
  }

  get isChangeValueAvailable(): boolean {
    return !isEmpty(this.args.changeValue);
  }

  get isPaceValueAvailable(): boolean {
    return !isEmpty(this.args.paceValue);
  }

  <template>
    <div class="flex flex-col gap-100">
      <div class="flex flex-row items-center gap-100">
        {{#if @source}}
          <a
            class="{{styles.title-link}} text-200--medium"
            data-test-id="smart-insight-kpi-block-label"
            href={{@source.href}}
            {{on "click" (fn @onSourceClick @source)}}
          >{{@label}}</a>
        {{else}}
          <span
            class="{{styles.title-text}} text-200--medium"
            data-test-id="smart-insight-kpi-block-label"
          >{{@label}}</span>
        {{/if}}

        {{#if @hover}}
          <button
            aria-label={{t
              "smart_insights.detail_view.kpi_hover.info_label"
              kpi=@label
            }}
            class={{styles.info-button}}
            data-test-id="smart-insight-kpi-block-info"
            type="button"
          >
            <PrismIcon @name="circle-information-outline" @size="extra-small" />

            <GeneralSmartInsightsDetailViewSmartInsightKpiHoverPanel
              @data={{@hover}}
              @title={{@label}}
            />
          </button>
        {{/if}}
      </div>

      <div class="flex flex-col">
        <div class="flex flex-row items-center gap-100">
          {{#if this.demandLevelClass}}
            <div class="identifier-square {{this.demandLevelClass}}">
              <PrismTooltip
                @text={{t
                  (concat
                    "market_insight.rate_level.level" this.demandLevelNumber
                  )
                }}
              />
            </div>
          {{/if}}
          <div
            class="text-400--bold"
            data-test-id="smart-insight-kpi-block-value"
          >{{@value}}</div>

          {{#if this.isChangeValueAvailable}}
            <GeneralSmartInsightsDetailViewSmartInsightKpiDelta
              @formatted={{@changeValueFormatted}}
              data-test-id="smart-insight-kpi-block-change"
            />
          {{else if (not @isChangeValueHidden)}}
            <span class="text-200--regular text-neutral-subdued">
              {{t "general.missing"}}
            </span>
          {{/if}}
        </div>

        {{#if (or this.isPaceValueAvailable (not @isPaceValueHidden))}}
          <div
            class="text-200--regular"
            data-test-id="smart-insight-kpi-block-pace"
          >
            <span class={{deltaColorClass @paceValue}}>
              {{#if this.isPaceValueAvailable}}
                {{@paceValueFormatted}}
              {{else}}
                {{t "general.missing"}}
              {{/if}}
            </span>
            <span class="text-neutral-subdued">
              {{t "smart_insights.detail_view.kpi_hover.vs_stly"}}
            </span>
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}
